import React, { useEffect, useState, useContext } from 'react';
import { NavigationContext } from '../../context/NavigationContext';
import { AuthContext } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import RechnungInfoCard from './rechnungDetails/RechnungInfoCard';
import PdfStatusCard from './rechnungDetails/PdfStatusCard';
import RoundTableInfoCard from './rechnungDetails/RoundTableInfoCard';
import PositionsSection from './rechnungDetails/PositionsSection';
import MemberListSection from './rechnungDetails/MemberListSection';
import InternalNotesSection from './rechnungDetails/InternalNotesSection';
import './RechnungDetails.css';

interface ProjectFile {
    id: number;
    filepath: string;
    projectId: number;
}

interface RechnungPosition {
    id: number;
    description: string;
    pricePerItem: number;
    numberOfItems: number;
    tax: number;
}

interface RoundTable {
    id: number;
    name: string;
    number: number;
    iban: string;
    bic: string;
    kontoInhaber: string;
    bankName: string;
    billingName: string;
    billingEmail: string;
    billingPlz: string;
    billingCity: string;
    billingStreet: string;
    billingStreetNumber: string;
}

interface Project {
    id: number;
    name: string;
    description: string | null;
    status: string;
}

interface Rechnung {
    id: number;
    amount: number;
    date: string;
    projectId: number;
    pdfPath?: string | null;
    internalNote?: string;
    project?: Project;
    roundTable?: RoundTable;
    rechnungPositions?: RechnungPosition[];
    projectFileUsed?: ProjectFile | null;
    projectFileFilter?: string | null;
}

const RechnungDetailsComponent: React.FC = () => {
    const navigationContext = useContext(NavigationContext);
    const authContext = useContext(AuthContext);
    const [rechnung, setRechnung] = useState<Rechnung | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    if (!navigationContext) {
        throw new Error('RechnungDetailsComponent must be used within a NavigationProvider');
    }
    if (!authContext) {
        throw new Error('RechnungDetailsComponent must be used within an AuthProvider');
    }

    const { path, navigateTo } = navigationContext;
    const { token } = authContext;

    // Extract rechnung ID from path (e.g., /rechnungdetails/1/5 where 1 is projectId and 5 is rechnungId)
    const pathParts = path.split('/rechnungdetails/')[1]?.split('/');
    const projectId = pathParts?.[0];
    const rechnungId = pathParts?.[1];

    useEffect(() => {
        const fetchRechnung = async () => {
            if (!rechnungId || isNaN(Number(rechnungId))) {
                setError('Ungültige Rechnungs-ID');
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(`/api/rechnungen/${rechnungId}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!response.ok) {
                    if (response.status === 404) {
                        throw new Error('Rechnung nicht gefunden');
                    }
                    throw new Error('Fehler beim Laden der Rechnung');
                }

                const data: Rechnung = await response.json();
                setRechnung(data);
                setError(null);
            } catch (error) {
                console.error('Error fetching rechnung:', error);
                setError(error instanceof Error ? error.message : 'Fehler beim Laden der Rechnung');
                toast.error(error instanceof Error ? error.message : 'Fehler beim Laden der Rechnung');
            } finally {
                setLoading(false);
            }
        };

        fetchRechnung();
    }, [rechnungId, token]);

    const handleBack = () => {
        if (projectId) {
            navigateTo(`/projectdetails/${projectId}`);
        } else if (rechnung?.projectId) {
            navigateTo(`/projectdetails/${rechnung.projectId}`);
        } else {
            navigateTo('/projects');
        }
    };

    if (loading) {
        return (
            <div className="rechnung-details-container">
                <div className="rechnung-details-loading">Lade Rechnungsdetails...</div>
            </div>
        );
    }

    if (error || !rechnung) {
        return (
            <div className="rechnung-details-container">
                <div className="rechnung-details-header">
                    <button className="back-button" onClick={handleBack} aria-label="Back to project details">
                        ←
                    </button>
                    <h1>Rechnungsdetails</h1>
                </div>
                <div className="rechnung-details-error">
                    <h2>Fehler</h2>
                    <p>{error || 'Rechnung nicht gefunden'}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="rechnung-details-container">
            <div className="rechnung-details-header">
                <button className="back-button" onClick={handleBack} aria-label="Back to project details">
                    ←
                </button>
                <h1>Rechnungsdetails</h1>
            </div>
            
            <div className="rechnung-details-content">
                <RechnungInfoCard rechnung={rechnung} />

                <PdfStatusCard 
                    rechnung={rechnung} 
                    onPdfCreated={(pdfPath) => setRechnung({ ...rechnung, pdfPath })} 
                />

                {rechnung.roundTable && (
                    <RoundTableInfoCard roundTable={rechnung.roundTable} />
                )}

                {rechnung.rechnungPositions && (
                    <PositionsSection positions={rechnung.rechnungPositions} />
                )}

                {rechnung.projectFileUsed && rechnung.projectFileFilter && rechnung.roundTable && (
                    <MemberListSection 
                        projectFile={rechnung.projectFileUsed}
                        projectFileFilter={rechnung.projectFileFilter}
                        roundTableNumber={rechnung.roundTable.number}
                    />
                )}

                <InternalNotesSection 
                    rechnungId={rechnung.id}
                    initialNote={rechnung.internalNote || ''}
                    onNoteSaved={(note) => setRechnung({ ...rechnung, internalNote: note })}
                />
            </div>
        </div>
    );
};

export default RechnungDetailsComponent;
